import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import DeleteIcon from "@mui/icons-material/Delete";
import PercentIcon from "@mui/icons-material/Percent";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CategoryIcon from "@mui/icons-material/Category";
import PublicIcon from "@mui/icons-material/Public";
import PreviewIcon from "@mui/icons-material/Preview";
import AdminHeader from "../../components/header/AdminHeader";

const SaleManagement = () => {
  const apiUrl = process.env.REACT_APP_API;
  const [subCategories, setSubCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Sale Form State
  const [saleType, setSaleType] = useState("subcategory"); // 'subcategory' or 'sitewide'
  const [discountType, setDiscountType] = useState("percentage"); // 'percentage' or 'fixed'
  const [discountValue, setDiscountValue] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");

  // Preview State
  const [previewProducts, setPreviewProducts] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Active Sales State
  const [activeSales, setActiveSales] = useState([]);

  useEffect(() => {
    fetchSubCategories();
    fetchActiveSales();
  }, []);

  const fetchSubCategories = async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/subcategories`);
      setSubCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.error("Failed to load subcategories");
      setSubCategories([]);
    }
  };

  const fetchActiveSales = async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/active-sales`);
      setActiveSales(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching active sales:", error);
      setActiveSales([]);
    }
  };

  const handlePreview = async () => {
    if (saleType === "subcategory" && !selectedSubCategory) {
      toast.error("Please select a subcategory");
      return;
    }

    if (!discountValue || discountValue <= 0) {
      toast.error("Please enter a valid discount value");
      return;
    }

    if (discountType === "percentage" && discountValue > 100) {
      toast.error("Percentage discount cannot exceed 100%");
      return;
    }

    try {
      setIsLoading(true);
      const { data } = await axios.post(`${apiUrl}/preview-sale`, {
        saleType,
        discountType,
        discountValue: parseFloat(discountValue),
        subCategoryId: saleType === "subcategory" ? selectedSubCategory : null,
      });

      setPreviewProducts(data.products);
      setShowPreview(true);
      setIsLoading(false);
      toast.success(`Preview ready: ${data.products.length} products will be affected`);
    } catch (error) {
      console.error("Preview error:", error);
      toast.error(error.response && error.response.data && error.response.data.message|| "Failed to generate preview");
      setIsLoading(false);
    }
  };

  const handleApplySale = async () => {
    if (saleType === "subcategory" && !selectedSubCategory) {
      toast.error("Please select a subcategory");
      return;
    }

    if (!discountValue || discountValue <= 0) {
      toast.error("Please enter a valid discount value");
      return;
    }

    if (discountType === "percentage" && discountValue > 100) {
      toast.error("Percentage discount cannot exceed 100%");
      return;
    }

    if (!window.confirm(
      `Are you sure you want to apply this sale?\n\n` +
      `Type: ${saleType === "sitewide" ? "Sitewide" : "Subcategory"}\n` +
      `Discount: ${discountValue}${discountType === "percentage" ? "%" : "$"}\n` +
      `Affected Products: ${previewProducts.length || "calculating..."}`
    )) {
      return;
    }

    try {
      setIsLoading(true);
      const { data } = await axios.post(`${apiUrl}/apply-sale`, {
        saleType,
        discountType,
        discountValue: parseFloat(discountValue),
        subCategoryId: saleType === "subcategory" ? selectedSubCategory : null,
      });

      toast.success(data.message);

      // Reset form
      setDiscountValue("");
      setSelectedSubCategory("");
      setShowPreview(false);
      setPreviewProducts([]);

      // Refresh active sales
      fetchActiveSales();
      setIsLoading(false);
    } catch (error) {
      console.error("Apply sale error:", error);
      toast.error(error.response && error.response.data && error.response.data.message|| "Failed to apply sale");
      setIsLoading(false);
    }
  };

  const handleRemoveSale = async (saleId, saleName) => {
    if (!window.confirm(`Are you sure you want to remove the sale on "${saleName}"? All products will revert to their original prices.`)) {
      return;
    }

    try {
      setIsLoading(true);
      const { data } = await axios.delete(`${apiUrl}/remove-sale/${saleId}`);
      toast.success(data.message);
      fetchActiveSales();
      setIsLoading(false);
    } catch (error) {
      console.error("Remove sale error:", error);
      toast.error(error.response && error.response.data && error.response.data.message || "Failed to remove sale");
      setIsLoading(false);
    }
  };

  const getSubCategoryName = (subCategoryData) => {
    // Handle both populated object and ID string
    if (typeof subCategoryData === 'object' && subCategoryData !== null) {
      return subCategoryData.name || "Unknown";
    }
    // If it's just an ID, find in subCategories array
    const subCat = subCategories.find(sc => sc._id === subCategoryData);
    return subCat ? subCat.name : "Unknown";
  };

  return (
    <>
      <AdminHeader />
      <div className="container-fluid my-4">

      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center mb-4">
            <LocalOfferIcon style={{ fontSize: "2.5rem", color: "#002E6A", marginRight: "10px" }} />
            <h1 style={{ color: "#002E6A", fontWeight: "700", margin: 0 }}>
              Sale Management
            </h1>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Sale Application Form */}
        <div className="col-lg-6 mb-4">
          <div className="card shadow-sm border-0">
            <div className="card-header" style={{ backgroundColor: "#002E6A", color: "white" }}>
              <h5 className="mb-0">
                <LocalOfferIcon className="me-2" />
                Apply New Sale
              </h5>
            </div>
            <div className="card-body p-4">
              {/* Sale Type Selection */}
              <div className="mb-4">
                <label className="form-label fw-bold">Sale Type</label>
                <div className="btn-group w-100" role="group">
                  <button
                    className={`btn ${saleType === "subcategory" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setSaleType("subcategory")}
                  >
                    <CategoryIcon className="me-1" /> By Subcategory
                  </button>
                  <button
                    className={`btn ${saleType === "sitewide" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setSaleType("sitewide")}
                  >
                    <PublicIcon className="me-1" /> Sitewide Sale
                  </button>
                </div>
              </div>

              {/* Subcategory Selection (only if subcategory sale type) */}
              {saleType === "subcategory" && (
                <div className="mb-4">
                  <label className="form-label fw-bold">Select Subcategory</label>
                  <select
                    className="form-select"
                    value={selectedSubCategory}
                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                  >
                    <option value="">-- Choose Subcategory --</option>
                    {Array.isArray(subCategories) && subCategories.map((subCat) => (
                      <option key={subCat._id} value={subCat._id}>
                        {subCat.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Discount Type Selection */}
              <div className="mb-4">
                <label className="form-label fw-bold">Discount Type</label>
                <div className="btn-group w-100" role="group">
                  <button
                    className={`btn ${discountType === "percentage" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => setDiscountType("percentage")}
                  >
                    <PercentIcon className="me-1" /> Percentage
                  </button>
                  <button
                    className={`btn ${discountType === "fixed" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => setDiscountType("fixed")}
                  >
                    <AttachMoneyIcon className="me-1" /> Fixed Amount
                  </button>
                </div>
              </div>

              {/* Discount Value */}
              <div className="mb-4">
                <label className="form-label fw-bold">
                  Discount Value {discountType === "percentage" ? "(%)" : "($)"}
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder={discountType === "percentage" ? "e.g., 20 for 20% off" : "e.g., 5 for $5 off"}
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  min="0"
                  max={discountType === "percentage" ? "100" : undefined}
                  step={discountType === "percentage" ? "1" : "0.01"}
                />
                {discountType === "percentage" && discountValue && (
                  <small className="text-muted">
                    Products will be sold at {100 - parseFloat(discountValue)}% of original price
                  </small>
                )}
              </div>

              {/* Action Buttons */}
              <div className="d-grid gap-2">
                <button
                  className="btn btn-info btn-lg"
                  onClick={handlePreview}
                  disabled={isLoading}
                >
                  <PreviewIcon className="me-2" />
                  {isLoading ? "Loading..." : "Preview Sale"}
                </button>
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleApplySale}
                  disabled={isLoading || previewProducts.length === 0}
                >
                  <LocalOfferIcon className="me-2" />
                  {isLoading ? "Applying..." : "Apply Sale"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Active Sales & Preview */}
        <div className="col-lg-6">
          {/* Preview Section */}
          {showPreview && previewProducts.length > 0 && (
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0">
                  <PreviewIcon className="me-2" />
                  Sale Preview ({previewProducts.length} products)
                </h5>
              </div>
              <div className="card-body" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <table className="table table-sm table-hover">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Product</th>
                      <th>Original</th>
                      <th>Sale Price</th>
                      <th>Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewProducts.slice(0, 50).map((product) => (
                      <tr key={product._id}>
                        <td style={{ fontSize: "0.85rem" }}>{product.productName}</td>
                        <td>${product.originalPrice.toFixed(2)}</td>
                        <td className="text-success fw-bold">${product.newSalePrice.toFixed(2)}</td>
                        <td className="text-danger">-${product.savings.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewProducts.length > 50 && (
                  <small className="text-muted">Showing first 50 of {previewProducts.length} products</small>
                )}
              </div>
            </div>
          )}

          {/* Active Sales Section */}
          <div className="card shadow-sm border-0">
            <div className="card-header" style={{ backgroundColor: "#28a745", color: "white" }}>
              <h5 className="mb-0">
                <LocalOfferIcon className="me-2" />
                Active Sales
              </h5>
            </div>
            <div className="card-body">
              {activeSales.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <LocalOfferIcon style={{ fontSize: "3rem", opacity: 0.3 }} />
                  <p className="mt-2 mb-0">No active sales</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {activeSales.map((sale) => (
                    <div key={sale._id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1 fw-bold">
                            {sale.saleType === "sitewide" ? (
                              <>
                                <PublicIcon className="me-1" style={{ fontSize: "1rem" }} />
                                Sitewide Sale
                              </>
                            ) : (
                              <>
                                <CategoryIcon className="me-1" style={{ fontSize: "1rem" }} />
                                {getSubCategoryName(sale.subCategoryId)}
                              </>
                            )}
                          </h6>
                          <p className="mb-1">
                            <span className="badge bg-success">
                              {sale.discountValue}{sale.discountType === "percentage" ? "%" : "$"} OFF
                            </span>
                          </p>
                          <small className="text-muted">
                            {sale.productsAffected} products affected
                          </small>
                          <br />
                          <small className="text-muted">
                            Applied: {new Date(sale.createdAt).toLocaleDateString()}
                          </small>
                        </div>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveSale(
                            sale._id,
                            sale.saleType === "sitewide" ? "Sitewide Sale" : getSubCategoryName(sale.subCategoryId)
                          )}
                          disabled={isLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SaleManagement;
